import { computed, inject, Injectable, signal } from '@angular/core';
import { cancelableResource } from '@softline/core';
import { CreditRepository } from '../repositories/credit.repository';
import { CustomerCreditBalance, DocumentCreditLimit } from '../types/credit';

/** Read-only credit state. Set `customerId` for the customer-only view; set
    `documentId` as well once a document is loaded — the document endpoint then
    takes over (it is the only one that returns the backend's `level`). */
@Injectable()
export class CreditService {
  private readonly creditRepository = inject(CreditRepository);

  readonly customerId = signal<string | null>(null);
  readonly documentId = signal<string | null>(null);
  /** Any value that changes when the document changed — the document balance
      goes stale with every position edit, so it must be refetched. */
  readonly revision = signal<unknown>(null);

  private readonly balanceError = signal(false);
  private readonly balanceResource = cancelableResource({
    // With a document bound the document endpoint is authoritative — it already
    // reports the customer balance, so this request is skipped.
    params: () => (this.documentId() ? null : this.customerId()),
    loader: async ({ params: id }) => {
      if (!id) {
        this.balanceError.set(false);
        return null;
      }
      try {
        const result = await this.creditRepository.getCustomerCreditBalance(id);
        this.balanceError.set(false);
        return result;
      } catch {
        this.balanceError.set(true);
        return null;
      }
    },
  });
  readonly creditBalance = computed<CustomerCreditBalance | null | undefined>(() =>
    this.balanceResource.value()
  );
  readonly creditBalanceLoading = computed(
    () => this.balanceResource.status() === 'loading'
  );
  readonly creditBalanceError = this.balanceError.asReadonly();

  private readonly limitError = signal(false);
  private readonly limitResource = cancelableResource({
    params: () => {
      const id = this.documentId();
      return id ? { id, revision: this.revision() } : null;
    },
    loader: async ({ params }) => {
      if (!params) {
        this.limitError.set(false);
        return null;
      }
      try {
        const result = await this.creditRepository.getDocumentCreditLimit(params.id);
        this.limitError.set(false);
        return result;
      } catch {
        this.limitError.set(true);
        return null;
      }
    },
  });
  readonly creditLimit = computed<DocumentCreditLimit | null | undefined>(() =>
    this.limitResource.value()
  );
  readonly creditLimitLoading = computed(() => this.limitResource.status() === 'loading');
  readonly creditLimitError = this.limitError.asReadonly();

  readonly isLoading = computed(
    () => this.creditBalanceLoading() || this.creditLimitLoading()
  );
}
